import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plane, Building, Users, CheckCircle2, XCircle, AlertCircle, Loader2, Info } from 'lucide-react'
import { useCart } from '@/lib/cart'
import { supabase } from '@/lib/supabase'
import { getCheckoutSession } from '@/lib/stripe-checkout'
import {
  IN_PROGRESS_KEYS,
  ORDER_STATUS_MESSAGES,
  itemStatusLabel,
  resolveOrderDisplay,
  type StatusTone,
} from './order-status-messages'
import { formatFlightRoute, type FlightItemData } from './flight-route'
// VOY-266: confirmBooking no longer called from frontend - Stripe webhook handles it
// import { confirmBooking } from '@/lib/api'

// VOY-571: the contact address for every contact line on this page (Juan's decision 2026-10-30).
// There is no /support route.
const SUPPORT_EMAIL = 'support@voy.earth'

const toneStyles: Record<StatusTone, { card: string; text: string; Icon: typeof Info }> = {
  pending: { card: 'border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800', text: 'text-blue-800 dark:text-blue-200', Icon: Loader2 },
  success: { card: 'border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800', text: 'text-green-800 dark:text-green-200', Icon: CheckCircle2 },
  warning: { card: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-800', text: 'text-yellow-800 dark:text-yellow-200', Icon: AlertCircle },
  error: { card: 'border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800', text: 'text-red-800 dark:text-red-200', Icon: XCircle },
  neutral: { card: 'border-gray-200 bg-gray-50 dark:bg-gray-950 dark:border-gray-800', text: 'text-gray-800 dark:text-gray-200', Icon: Info },
}

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams()
  const { clearCart } = useCart()
  // VOY-266: confirmingRef no longer needed - frontend doesn't call confirmBooking
  // const confirmingRef = useRef(false)
  type OrderItem = {
    id?: string
    status?: string
    item_type?: string
    refund_status?: string | null
    item_data?: FlightItemData & { name?: string }
    passengers?: Array<{ given_name?: string; family_name?: string }>
    guests?: Array<{ first_name?: string; last_name?: string }>
  }

  const [orderDetails, setOrderDetails] = useState<{
    id?: string
    amount_total?: number
    customer_email?: string
    customer_name?: string
    items?: OrderItem[]
    totalPassengers?: number
    totalGuests?: number
    stripe_session?: boolean
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // VOY-571: the order's stored status and error_message, never assumed. null until the order row
  // is read; a missing order (load error, Stripe session only, no id) shows the "unknown" message.
  const [status, setStatus] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // VOY-571: the order being polled (from ?order_id, or from the Stripe session's success_url) and
  // whether it reached a state it cannot leave, so polling stops there.
  const [pollOrderId, setPollOrderId] = useState<string | null>(null)
  const [pollingDone, setPollingDone] = useState(false)

  const sessionId = searchParams.get('session_id')
  const orderId = searchParams.get('order_id')

  useEffect(() => {
    // VOY-309: Clear cart first so it wins any race with CartProvider's loadCart
    clearCart()
    if (orderId) {
      fetchOrderFromDatabase(orderId)
      setPollOrderId(orderId)
    } else if (sessionId) {
      fetchStripeSessionAndOrder(sessionId)
    } else {
      setIsLoading(false)
    }
    // clearCart, fetchOrderFromDatabase are stable; deps intentionally [sessionId, orderId] only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, orderId])

  useEffect(() => {
    if (!pollOrderId || pollingDone) return
    // Poll every 2 seconds until the order reaches a final state (pollStatus sets pollingDone)
    const interval = setInterval(() => {
      pollStatus(pollOrderId)
    }, 2000)
    return () => clearInterval(interval)
    // pollStatus is stable; deps intentionally [pollOrderId, pollingDone] only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollOrderId, pollingDone])

  const fetchOrderFromDatabase = async (orderId: string) => {
    try {
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            *
          )
        `)
        .eq('id', orderId)
        .single()

      if (orderError) throw orderError

      // Calculate totals from order items
      let totalPassengers = 0;
      let totalGuests = 0;
      const items = order.order_items || [];

      items.forEach((item: OrderItem) => {
        if (item.passengers && Array.isArray(item.passengers)) {
          totalPassengers += item.passengers.length;
        }
        if (item.guests && Array.isArray(item.guests)) {
          totalGuests += item.guests.length;
        }
      });

      setOrderDetails({
        id: order.id,
        amount_total: order.total_amount * 100,
        customer_email: order.customer_email,
        customer_name: order.customer_name,
        items: items,
        totalPassengers,
        totalGuests,
      })
      setStatus(order.status ?? null)
      setErrorMessage(order.error_message ?? null)
      if (!IN_PROGRESS_KEYS.has(resolveOrderDisplay(order.status, order.error_message))) {
        setPollingDone(true)
      }

      // After payment is confirmed, trigger booking confirmation with suppliers
      // Only call if order is paid/created but not yet confirmed/confirming/holding
      // VOY-266: Frontend no longer calls confirmBooking - let Stripe webhook handle it
      // This prevents race conditions where frontend saves ratehawk_order_id before webhook arrives
      // The Stripe webhook will call /api/orchestration/confirm after payment success
      // Frontend only polls for status updates
      console.log('[Checkout Success] Waiting for Stripe webhook to trigger booking confirmation...')
    } catch (error) {
      console.error('Failed to fetch order details:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const pollStatus = async (orderId: string) => {
    try {
      // VOY-571: error_message tells a review case apart from a plain failure, and item statuses
      // change while the booking is confirmed, so both are polled with the order status.
      const { data: order, error } = await supabase
        .from('orders')
        .select('status, error_message, order_items ( id, status, refund_status )')
        .eq('id', orderId)
        .single()

      if (error) {
        console.debug('[Checkout Success] Poll error:', error.message)
        return
      }

      if (order?.status) {
        if (!IN_PROGRESS_KEYS.has(resolveOrderDisplay(order.status, order.error_message))) {
          setPollingDone(true)
        }
        setErrorMessage(order.error_message ?? null)
        setStatus(prevStatus => {
          if (prevStatus !== order.status) {
            console.log('[Checkout Success] Status changed:', prevStatus, '→', order.status)
          }
          return order.status
        })
        const polledItems: Array<{ id?: string; status?: string; refund_status?: string | null }> = order.order_items || []
        setOrderDetails(prev => {
          if (!prev?.items) return prev
          return {
            ...prev,
            items: prev.items.map(item => {
              const polled = polledItems.find(p => p.id && p.id === item.id)
              if (!polled) return item
              return polled.status !== item.status || polled.refund_status !== item.refund_status
                ? { ...item, status: polled.status, refund_status: polled.refund_status }
                : item
            }),
          }
        })
      }
    } catch (error) {
      // Silently fail - polling will continue
      console.debug('[Checkout Success] Poll exception:', error)
    }
  }

  const fetchStripeSessionAndOrder = async (sessionId: string) => {
    try {
      const stripeSession = await getCheckoutSession(sessionId)

      const successUrl = new URL(stripeSession?.success_url || window.location.href)
      const orderIdFromUrl = successUrl.searchParams.get('order_id')

      if (orderIdFromUrl) {
        await fetchOrderFromDatabase(orderIdFromUrl)
        setPollOrderId(orderIdFromUrl)
      } else {
        // VOY-571: without an order row there is no booking state to show; the card says so
        // instead of repeating Stripe's payment_status.
        setOrderDetails({
          id: sessionId,
          amount_total: stripeSession?.amount_total,
          customer_email: stripeSession?.customer_email || stripeSession?.customer_details?.email,
          stripe_session: true
        })
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Failed to fetch session details:', error)
      setIsLoading(false)
    }
  }

  const getItemTypeName = (item: OrderItem): string => {
    const itemData = item.item_data;
    if (item.item_type === 'flight') {
      // VOY-571: outbound slice as "City (IATA)" plus round trip / multi-city, for string or
      // Duffel-object endpoints, falling back to item_data.origin/destination (flight-route.ts).
      return formatFlightRoute(itemData);
    } else {
      return itemData?.name || 'Hotel Booking';
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-12">
        <div className="max-w-2xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading your order details...</p>
        </div>
      </div>
    )
  }

  const displayKey = resolveOrderDisplay(status, errorMessage)
  const display = ORDER_STATUS_MESSAGES[displayKey]
  const tone = toneStyles[display.tone]
  const StatusIcon = tone.Icon
  const reference = orderId || sessionId || undefined

  return (
    <div className="container mx-auto py-12">
      <div className="max-w-2xl mx-auto">
        {/* VOY-571: order status card, from the stored order status and error_message */}
        <div
          className={`rounded-lg border p-6 text-center ${tone.card}`}
          role="status"
          data-testid="order-status"
          data-status={displayKey}
        >
          <div className="flex justify-center mb-4">
            <StatusIcon className={`h-16 w-16 ${tone.text} ${IN_PROGRESS_KEYS.has(displayKey) ? 'animate-spin' : ''}`} aria-hidden="true" />
          </div>
          <h2 className={`text-2xl font-semibold ${tone.text}`}>{display.title}</h2>
          <p className={`mt-2 ${tone.text} opacity-90`}>{display.message}</p>
          {reference && (
            <div className="mt-4 bg-white dark:bg-gray-900 rounded-lg p-3 flex justify-between items-center text-sm">
              <span className="text-muted-foreground">Order ID:</span>
              <span className="font-mono text-xs">{reference}</span>
            </div>
          )}
          {display.showContact && (
            <p className={`mt-4 text-sm ${tone.text}`}>
              Contact us at{' '}
              <a className="underline font-medium" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
          )}
        </div>

        {/* Additional order details below status card */}
        {orderDetails && (
          <div className="mt-6 space-y-4">
            {/* Order Summary */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-4 space-y-3 border">
              <h3 className="font-semibold text-lg">Order Summary</h3>
              <div className="flex justify-between text-sm">
                <span>Total Amount:</span>
                <span className="font-semibold">
                  ${((orderDetails.amount_total || 0) / 100).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Traveler Summary */}
            {((orderDetails.totalPassengers || 0) > 0 || (orderDetails.totalGuests || 0) > 0) && (
              <div className="bg-white dark:bg-gray-900 rounded-lg p-4 space-y-3 border">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Travelers
                </h3>
                {(orderDetails.totalPassengers || 0) > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Plane className="h-4 w-4 text-muted-foreground" />
                      Flight Passengers
                    </span>
                    <span className="font-semibold">{orderDetails.totalPassengers}</span>
                  </div>
                )}
                {(orderDetails.totalGuests || 0) > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      Hotel Guests
                    </span>
                    <span className="font-semibold">{orderDetails.totalGuests}</span>
                  </div>
                )}
              </div>
            )}

            {/* Order Items Detail */}
            {orderDetails.items && orderDetails.items.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-lg p-4 space-y-3 border">
                <h3 className="font-semibold text-lg">Booking Details</h3>
                    {orderDetails.items.map((item, index: number) => (
                  <div key={item.id ?? index} className="border-l-4 border-primary pl-3 py-2">
                    <div className="flex items-center gap-2 mb-1">
                      {item.item_type === 'flight' ? (
                        <Plane className="h-4 w-4 text-primary" />
                      ) : (
                        <Building className="h-4 w-4 text-primary" />
                      )}
                      <span className="font-medium text-sm">
                        {getItemTypeName(item)}
                      </span>
                      {/* VOY-571: each item's stored status */}
                      <span className="ml-auto text-xs font-medium text-muted-foreground" data-testid="item-status">
                        {itemStatusLabel(item.status, item.refund_status)}
                      </span>
                    </div>
                    {item.passengers && item.passengers.length > 0 && (
                      <p className="text-xs text-muted-foreground ml-6">
                        {item.passengers.length} passenger{item.passengers.length !== 1 ? 's' : ''}: {
                          item.passengers
                            .map((p) => `${p.given_name || ''} ${p.family_name || ''}`.trim())
                            .join(', ')
                        }
                      </p>
                    )}
                    {item.guests && item.guests.length > 0 && (
                      <p className="text-xs text-muted-foreground ml-6">
                        {item.guests.length} guest{item.guests.length !== 1 ? 's' : ''}: {
                          item.guests
                            .map((g) => `${g.first_name || ''} ${g.last_name || ''}`.trim())
                            .join(', ')
                        }
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
